import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useState, useEffect, useContext } from "react";
import liff from "@line/liff";
import axios from "axios";
import { SubscribeModalContext } from "../../providers/SubscribeModalProvider";
import { ensureLiffInit } from "../../liffClient";

interface Props {
  refreshKey?: number;
}

export default function WordCard({ refreshKey }: Props) {
  const [userId, setUserId] = useState<string | null>(null);
  const [wordResponse, setWordResponse] = useState<any | null>({
    Item: { words: [] },
  });

  const { setOpen } = useContext(SubscribeModalContext);

  useEffect(() => {
    const fetchWordData = async () => {
      try {
        await ensureLiffInit();
        const profile = await liff.getProfile();
        const userId = profile.userId;
        setUserId(userId);
        const response = await axios.post("/v1/api/word/fetch", {
          userId: profile.userId,
        });

        if (!response.data.Item) {
          return;
        }

        setWordResponse(response.data);

        if (
          !response.data.Item.expire_date ||
          new Date(response.data.Item.expire_date) < new Date()
        ) {
          setOpen(true);
        }
      } catch (error) {
        console.error("Error fetching word data:", error);
      }
    };

    fetchWordData();
  }, [refreshKey]);

  const speakText = (word: string) => {
    const instance = new SpeechSynthesisUtterance(word);
    instance.lang = wordResponse.Item.studying_language;
    window.speechSynthesis.speak(instance);
  };

  const deleteText = (word: string, index: string) => {
    axios
      .post("/v1/api/word/delete", {
        userId: userId,
        word: word,
      })
      .then(() => {
        const block = document.getElementById(word + "_" + index);
        block?.remove();
      })
      .catch((error) => {
        console.error("Error deleting word:", error);
      });
  };

  return (
    <Box>
      {(wordResponse.Item?.words ?? []).map((item: any, i: number) => (
        <Box
          id={item.word + "_" + i}
          key={i}
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            mb: 2.5,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            border: "1px solid rgba(226,232,240,0.8)",
            overflow: "hidden",
            transition: "box-shadow 0.2s, transform 0.2s",
            "&:hover": {
              boxShadow: "0 6px 24px rgba(67,56,202,0.12)",
              transform: "translateY(-2px)",
            },
            display: "flex",
            alignItems: "stretch",
          }}
        >
          {/* Left accent bar */}
          <Box
            sx={{
              width: 5,
              background: "linear-gradient(180deg, #4338ca, #818cf8)",
              flexShrink: 0,
            }}
          />

          {/* Main content */}
          <Box
            sx={{
              flex: 1,
              p: 2.5,
              display: "flex",
              alignItems: "center",
              gap: 2,
              minWidth: 0,
            }}
          >
            {/* Speaker button */}
            <IconButton
              onClick={() => speakText(item.word)}
              sx={{
                width: 44,
                height: 44,
                background: "linear-gradient(135deg, #4338ca, #6366f1)",
                color: "white",
                flexShrink: 0,
                boxShadow: "0 3px 10px rgba(99,102,241,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #3730a3, #4f46e5)",
                  transform: "scale(1.05)",
                },
                transition: "transform 0.15s",
              }}
            >
              <VolumeUpIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {/* Word & Translation */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: "#1e293b",
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                }}
              >
                {item.word}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mt: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 1.5,
                    backgroundColor: "#e2e8f0",
                    borderRadius: 1,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.88rem",
                    color: "#64748b",
                    fontWeight: 500,
                    wordBreak: "break-word",
                  }}
                >
                  {item.translated}
                </Typography>
              </Box>
            </Box>

            {/* Image */}
            {item.image_url && (
              <Box
                component="img"
                src={item.image_url}
                alt="word-image"
                sx={{
                  width: 72,
                  height: 72,
                  objectFit: "cover",
                  borderRadius: "12px",
                  flexShrink: 0,
                  border: "2px solid #f1f5f9",
                }}
              />
            )}

            {/* Delete button */}
            <IconButton
              onClick={() => deleteText(item.word, i.toString())}
              sx={{
                width: 36,
                height: 36,
                color: "#cbd5e1",
                flexShrink: 0,
                "&:hover": {
                  color: "#ef4444",
                  backgroundColor: "#fef2f2",
                },
                transition: "color 0.15s, background-color 0.15s",
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
